import React, { useState, useEffect, useRef } from "react";
import {
  Box, Flex, Text, Badge, Spinner, SimpleGrid, Stat, StatLabel, StatNumber,
  Card, CardHeader, CardBody, Collapse, useTheme, VStack, Container,
} from "@chakra-ui/react";
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import { FaChartLine, FaDatabase, FaExchangeAlt, FaStoreAlt, FaCopy, FaFileAlt, FaShieldAlt } from "react-icons/fa";
import axios from "axios";

import MissingStores from "./MissingStores";
import DuplicateStores from "./DuplicateStores";
import InconsistentDescriptions from "./InconsistentDescriptions";
import ValidationTrend from "./ValidationTrend";
import SohCoverage from "./SohCoverage";
import WhatsNew from "./WhatsNew";

const API = process.env.REACT_APP_API_URL;

// Scroll-reveal hook (same pattern as Sales dashboard)
const useScrollReveal = () => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setIsVisible(true); observer.unobserve(node); } },
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px" }
    );
    observer.observe(node);
    return () => observer.unobserve(node);
  }, []);
  return [ref, isVisible];
};

// Count-up animation hook
const useCountUp = (target, duration = 600) => {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (target === null || target === undefined) return;
    let start = 0;
    const startTime = performance.now();
    const animate = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(eased * target));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [target, duration]);
  return value;
};

// KPI Stat Card
const KpiCard = ({ label, value, suffix = "", color, icon: Icon, loading }) => {
  const animatedValue = useCountUp(loading ? 0 : value);
  const isGood = value === 0 || (suffix === "%" && value >= 95);
  return (
    <Box
      p={4}
      bg="white"
      borderRadius="lg"
      boxShadow="sm"
      borderLeft="4px solid"
      borderLeftColor={loading ? "gray.200" : isGood ? "green.400" : "red.400"}
      transition="all 0.3s ease"
      _hover={{ transform: "translateY(-2px)", boxShadow: "md" }}
      position="relative"
      overflow="hidden"
    >
      {loading ? (
        <Flex justify="center" py={2}><Spinner size="sm" /></Flex>
      ) : (
        <Stat>
          <Flex justify="space-between" align="center">
            <Box>
              <StatLabel fontSize="xs" textTransform="uppercase" color="gray.500" fontWeight="600">{label}</StatLabel>
              <StatNumber fontSize="2xl" color={isGood ? "green.500" : "red.500"}>
                {suffix === "%" ? animatedValue + suffix : animatedValue}
              </StatNumber>
            </Box>
            {Icon && <Icon size={20} color={isGood ? "#38A169" : "#E53E3E"} opacity={0.3} />}
          </Flex>
        </Stat>
      )}
    </Box>
  );
};

// Accordion section matching Sales dashboard pattern
const AccordionSection = ({ title, icon: Icon, isOpen, onToggle, badge, badgeColor, children, index = 0 }) => {
  const theme = useTheme();
  const [revealRef, isVisible] = useScrollReveal();
  return (
    <Box
      ref={revealRef}
      opacity={isVisible ? 1 : 0}
      transform={isVisible ? "translateY(0)" : "translateY(30px)"}
      transition={`opacity 0.6s ease ${index * 0.1}s, transform 0.6s ease ${index * 0.1}s`}
    >
      <Card mb={3} boxShadow={isOpen ? "md" : "sm"} borderWidth="1px" borderColor="gray.200" transition="all 0.3s ease" _hover={{ boxShadow: "md" }}>
        <CardHeader
          bg={`linear-gradient(135deg, ${theme.colors.primary1}, ${theme.colors.primary2})`}
          color="white"
          borderTopRadius="md"
          py={2.5}
          px={4}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          cursor="pointer"
          onClick={onToggle}
          _hover={{ bg: `linear-gradient(135deg, ${theme.colors.primary2}, ${theme.colors.primary1})` }}
        >
          <Flex align="center" gap={3}>
            {Icon && <Icon size={14} />}
            <Text fontWeight="600" fontSize="sm">{title}</Text>
          </Flex>
          <Flex align="center" gap={2}>
            {badge !== undefined && (
              <Badge bg="whiteAlpha.200" color="white" fontSize="xs" px={2} py={0.5} borderRadius="full">
                {badge}
              </Badge>
            )}
            {isOpen ? <ChevronUpIcon boxSize={5} /> : <ChevronDownIcon boxSize={5} />}
          </Flex>
        </CardHeader>
        <Collapse in={isOpen} animateOpacity>
          <CardBody p={4}>{children}</CardBody>
        </Collapse>
      </Card>
    </Box>
  );
};

const DataQuality = () => {
  const [sections, setSections] = useState({
    trend: true,
    coverage: false,
    whatsNew: false,
    duplicates: false,
    inconsistent: false,
    missing: false,
  });

  const [counts, setCounts] = useState({ duplicates: null, missing: null, inconsistent: null, coverage: null });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCounts();
  }, []);

  const fetchCounts = async () => {
    try {
      const [dupes, missing, inconsistent, coverage] = await Promise.all([
        axios.post(`${API}/validation/duplicate-stores`, {}).catch(() => ({ data: { data: [] } })),
        axios.post(`${API}/validation/get-missing-stores`, {}).catch(() => ({ data: { data: [] } })),
        axios.post(`${API}/validation/inconsistent-descriptions`, {}).catch(() => ({ data: { data: [] } })),
        axios.post(`${API}/validation/soh-coverage`, {}).catch(() => ({ data: { data: [] } })),
      ]);

      const covData = coverage.data?.data || [];
      const avgCoverage = covData.length > 0
        ? Math.round(covData.reduce((s, r) => s + parseFloat(r.coverage_pct || 0), 0) / covData.length * 10) / 10
        : 0;

      setCounts({
        duplicates: dupes.data?.data?.length || 0,
        missing: missing.data?.data?.length || 0,
        inconsistent: inconsistent.data?.data?.length || 0,
        coverage: avgCoverage,
      });
    } finally {
      setLoading(false);
    }
  };

  const toggle = (key) => setSections((p) => ({ ...p, [key]: !p[key] }));

  return (
    <Box minW="75vw">
      <Flex justify="space-between" align="center" mb={5}>
        <Flex align="center" gap={3}>
          <FaShieldAlt size={20} color="#58b6c0" />
          <Text fontSize="xl" fontWeight="700">Data Quality Dashboard</Text>
        </Flex>
      </Flex>

      {/* KPI Cards */}
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={6}>
        <KpiCard label="Duplicate Stores" value={counts.duplicates} icon={FaCopy} loading={loading} />
        <KpiCard label="Missing Stores" value={counts.missing} icon={FaStoreAlt} loading={loading} />
        <KpiCard label="Inconsistent Desc." value={counts.inconsistent} icon={FaFileAlt} loading={loading} />
        <KpiCard label="Avg SOH Coverage" value={counts.coverage} suffix="%" icon={FaDatabase} loading={loading} />
      </SimpleGrid>

      {/* Accordion Sections */}
      <VStack spacing={0} align="stretch">
        <AccordionSection title="Validation Trend (30 days)" icon={FaChartLine} isOpen={sections.trend} onToggle={() => toggle("trend")} index={0}>
          <ValidationTrend />
        </AccordionSection>

        <AccordionSection title="SOH Coverage by Retailer" icon={FaDatabase} isOpen={sections.coverage} onToggle={() => toggle("coverage")} index={1}>
          <SohCoverage />
        </AccordionSection>

        <AccordionSection title="What's New (since last check)" icon={FaExchangeAlt} isOpen={sections.whatsNew} onToggle={() => toggle("whatsNew")} index={2}>
          <WhatsNew />
        </AccordionSection>

        <AccordionSection
          title="Duplicate Store Entries — store_details table"
          icon={FaCopy}
          isOpen={sections.duplicates}
          onToggle={() => toggle("duplicates")}
          badge={counts.duplicates}
          index={3}
        >
          <DuplicateStores onCountChange={(c) => setCounts((p) => ({ ...p, duplicates: c }))} />
        </AccordionSection>

        <AccordionSection
          title="Inconsistent Site Descriptions — sales data"
          icon={FaFileAlt}
          isOpen={sections.inconsistent}
          onToggle={() => toggle("inconsistent")}
          badge={counts.inconsistent}
          index={4}
        >
          <InconsistentDescriptions onCountChange={(c) => setCounts((p) => ({ ...p, inconsistent: c }))} />
        </AccordionSection>

        <AccordionSection
          title="Missing Stores — store_details table"
          icon={FaStoreAlt}
          isOpen={sections.missing}
          onToggle={() => toggle("missing")}
          badge={counts.missing}
          index={5}
        >
          <MissingStores />
        </AccordionSection>
      </VStack>
    </Box>
  );
};

export default DataQuality;
